import React, { useState, useEffect, useCallback } from 'react';
import WebFont from 'webfontloader';
import { connect } from 'react-redux';

import * as Store from '@apply/store';
import {
    BackButtonWithStore,
    EsignModal,
    fonts,
    ShowIf,
    Question,
    QuestionType,
    FormValidationMetadata,
    formatDate,
    formatDateTime,
    Font,
} from '@apply/ui';
import { applicationInsights } from '../ApplicationInsights';
import { LoanType } from '@apply/constants';

export interface ReviewSubmissionProps {
    state?: Store.ApplyRootStateType;
    actions?: Actions;
    esign?: Store.BorrowerStore.Types.Esign;
    printMode?: boolean;
    onUpdateEsign?: (esign: Store.BorrowerStore.Types.Esign) => void;
    onSubmitClick?: () => void;
    formCompleted?: boolean;
    onNeedIpAddress?: () => Promise<string>;
}

const getTermSheetHtml = async (
    logoUrl: string,
    rentalExhibitImageUrl: string,
    loanType: number,
    borrowerName: string,
    borrowerEmail: string,
    borrowerEntity: string,
    borrowerPhone: string,
    borrowerTitle: string
) =>
    await Store.CrossStore.getTermSheetHtml(
        logoUrl,
        rentalExhibitImageUrl,
        loanType,
        borrowerName,
        borrowerEmail,
        borrowerEntity,
        borrowerPhone,
        borrowerTitle
    );

export const ReviewSubmission: React.FC<ReviewSubmissionProps> = ({
    state,
    actions,
    esign,
    onUpdateEsign,
    onSubmitClick,
    formCompleted,
    onNeedIpAddress,
}) => {
    const [validation, setValidation] = useState<FormValidationMetadata>({});

    const [typedName, setTypedName] = useState<string>(esign?.typedName || '');
    const [title, setTitle] = useState<string>(esign?.title || '');
    const [date] = useState<Date>(esign?.date || new Date());
    const [ipAddress, setIpAddress] = useState<string>(esign?.ipAddress || '');
    const [font, setFont] = useState<Font>(undefined);

    const [acknowledgeAgreement, setAcknowledgeAgreement] = useState<boolean>(false);
    const [acknowledgeHtml, setAcknowledgeHtml] = useState<string>('');
    const [acknowledgeModalOpen, setAcknowledgeModalOpen] = useState<boolean>(false);

    const [termSheetAgreement, setTermSheetAgreement] = useState<boolean>(false);
    const [termSheetHtml, setTermSheetHtml] = useState<string>('');
    const [termSheetModalOpen, setTermSheetModalOpen] = useState<boolean>(false);

    const onQuestionValidate = useCallback((selector: string, passing: boolean, message: string) => {
        setValidation((state) => {
            const record = {
                ...state[selector],
                passing,
                failureMessage: message,
            };
            return {
                ...state,
                [selector]: record,
            };
        });
    }, []);
    const [submitted, setSubmitted] = useState(false);
    const submitButtonClick = () => {
        setSubmitted(true);
        const failureMessages: string[] = [];
        for (const fieldName in validation) {
            if (!validation[fieldName]?.passing) {
                /* istanbul ignore else */
                if (validation[fieldName]?.failureMessage) {
                    failureMessages.push(validation[fieldName]?.failureMessage);
                }
            }
        }
        if (failureMessages.length > 0) {
            console.error('form validation failed', failureMessages);
        } else {
            onSubmitClick();
        }
    };

    useEffect(() => {
        onUpdateEsign &&
            onUpdateEsign({
                typedName,
                title,
                date,
                acknowledgeFont: font?.id,
                eSignFonts: {
                    acknowledgeFont: font?.id,
                    eSignFont: font?.id,
                    termSheetFont: font?.id,
                    typedName: typedName,
                    title: title,
                },
                termsheetFont: font?.id,
                ipAddress: ipAddress ?? 'unknown',
            });
    }, [onUpdateEsign, typedName, title, date, font, ipAddress]);

    useEffect(() => {
        let canceled = false;

        if (onNeedIpAddress && !esign?.ipAddress) {
            onNeedIpAddress().then((ip) => {
                if (!canceled) {
                    if (!ip) {
                        applicationInsights.appInsights.trackException({
                            error: new Error('Failed to retrieve IP address! External service failed to return IP address.'),
                            severityLevel: 3,
                        });
                    }
                    setIpAddress(ip);
                }
            });
        }

        return () => {
            canceled = true;
        };
    }, [onNeedIpAddress, esign?.ipAddress]);

    useEffect(() => {
        (async () => {
            const fontNames = fonts.map((font) => font.name);
            WebFont.load({
                google: {
                    families: fontNames,
                },
            });
        })();
    }, []);

    const onClickAcknowledgement = () => {
        const acknowledgeAgreementCheckbox = document.getElementById('acknowledgeAgreement') as HTMLInputElement;
        if (acknowledgeAgreementCheckbox) acknowledgeAgreementCheckbox.checked = acknowledgeAgreement;

        const template =
            state.borrower.loanType == LoanType.Rental ? rentalAcknowledgementTemplate : generalAcknowledgementTemplate;
        setAcknowledgeHtml(constructAgreementHtml(template, typedName, title, date, null, ipAddress));
        setAcknowledgeModalOpen(true);
    };

    const onClickTermSheetAgreement = async () => {
        const termSheetAgreementCheckbox = document.getElementById('termSheetAgreement') as HTMLInputElement;
        if (termSheetAgreementCheckbox) termSheetAgreementCheckbox.checked = termSheetAgreement;

        const loanTypeId = state.borrower.loanType ?? 1;
        await getTermSheetHtml(
            `${window.location.origin}/assets/img/header-logo.png`, // TODO: Refactor to use Admin URL constants
            `${window.location.origin}/assets/img/rental-exhibit.png`,
            loanTypeId,
            typedName,
            state.borrower.principal.email,
            state.borrower.entity.name,
            state.borrower.principal.phone,
            title
        ).then((agreementHtml) => {
            setTermSheetHtml(constructAgreementHtml(agreementHtml, typedName, title, date, null, ipAddress));
            setTermSheetModalOpen(true);
        });
    };

    const onConfirmAcknowledge = (font: Font) => {
        setAcknowledgeModalOpen(false);

        if (font) {
            setFont(font);
            setAcknowledgeAgreement(true);
        }
    };

    const onConfirmTermsheet = (font: Font) => {
        setTermSheetModalOpen(false);

        if (font) {
            setFont(font);
            setTermSheetAgreement(true);
        }
    };

    const onRejectAcknowledge = () => {
        setAcknowledgeModalOpen(false);
        setAcknowledgeAgreement(false);
    };

    const onRejectTermsheet = () => {
        setTermSheetModalOpen(false);
        setTermSheetAgreement(false);
    };

    const canSubmit = () => {
        if (state.borrower.loanType === LoanType.SubNoteConstruction || state.borrower.loanType === LoanType.SubNoteFixFlip) {
            //Doesn't need termsheet or finalizeThirdPartyClaim
            return !typedName || !title || !date || !acknowledgeAgreement;
        } else {
            return (
                !typedName ||
                !title ||
                !date ||
                !acknowledgeAgreement ||
                !termSheetAgreement ||
                !state.borrower.finalizeThirdPartyClaim ||
                (state.borrower.finalizeThirdPartyClaim === 'yes' && !state.borrower.finalizeThirdPartyName)
            );
        }
    };

    const styles = {
        checkbox: {
            height: '15px',
            marginLeft: '20px',
            marginRight: '5px',
        },
        checkboxLabel: {
            width: '90%',
        },
    };

    return (
        <>
            <div className='row'>
                <div className='columns'>
                    <label htmlFor='typedname'>Typed name</label>
                    <ShowIf showIf={!formCompleted}>
                        <input
                            id='typedname'
                            type='text'
                            value={typedName}
                            onChange={(e) => setTypedName(e.target.value)}
                        />
                    </ShowIf>
                    <ShowIf showIf={formCompleted}>
                        <div>{typedName}</div>
                    </ShowIf>
                </div>
            </div>
            <div className='row margin-top'>
                <div className='columns'>
                    <label htmlFor='title'>Title</label>
                    <ShowIf showIf={!formCompleted}>
                        <input
                            id='title'
                            type='text'
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                        />
                    </ShowIf>
                    <ShowIf showIf={formCompleted}>
                        <div>{title}</div>
                    </ShowIf>
                </div>
                <div className='columns medium-4'>
                    <label htmlFor='date'>Date</label>
                    <ShowIf showIf={!formCompleted}>
                        <input
                            id='date'
                            type='date'
                            data-testid='esign-date-input'
                            value={formatDate(date)}
                            disabled={true}
                        />
                    </ShowIf>
                    <ShowIf showIf={formCompleted}>
                        <div>{formatDate(date)}</div>
                    </ShowIf>
                </div>
            </div>
            <div className='row margin-top'>
                <div
                    className='columns small-5'
                    style={{ whiteSpace: 'nowrap' }}
                >
                    <input
                        id='acknowledgeAgreement'
                        type='checkbox'
                        onChange={(event) => setAcknowledgeAgreement(event.target.checked)}
                        onClick={onClickAcknowledgement}
                        checked={acknowledgeAgreement}
                        disabled={!typedName || !title}
                        style={styles.checkbox}
                    />
                    <label
                        className={!typedName || !title ? 'link disabled' : 'link'}
                        htmlFor='acknowledgeAgreement'
                        style={styles.checkboxLabel}
                    >
                        Agree to the acknowledgement
                    </label>
                </div>
            </div>
            <ShowIf
                showIf={
                    state.borrower.loanType !== LoanType.SubNoteConstruction &&
                    state.borrower.loanType !== LoanType.SubNoteFixFlip
                }
            >
                <div className='row margin-top'>
                    <div className='columns small-12'>
                        <input
                            id='termSheetAgreement'
                            type='checkbox'
                            onChange={(event) => setTermSheetAgreement(event.target.checked)}
                            onClick={onClickTermSheetAgreement}
                            checked={termSheetAgreement}
                            disabled={!typedName || !title}
                            style={styles.checkbox}
                        />
                        <label
                            className={!typedName || !title ? 'link disabled' : 'link'}
                            htmlFor='termSheetAgreement'
                            style={styles.checkboxLabel}
                        >
                            Agree to the term sheet (a copy will be emailed to you upon submission)
                        </label>
                    </div>
                </div>
            </ShowIf>
            <div className='form-group'>
                <ShowIf
                    showIf={
                        state.borrower.loanType !== LoanType.SubNoteConstruction &&
                        state.borrower.loanType !== LoanType.SubNoteFixFlip
                    }
                >
                    <div className='row'>
                        <Question
                            type={QuestionType.YesNoUnsure}
                            selector='party-claim'
                            styleName='ungrouped'
                            onChange={actions?.onChangeFinalizeThirdPartyClaim}
                            value={state.borrower?.finalizeThirdPartyClaim}
                        >
                            Does a broker or other third party reasonably expect compensation for arranging this loan?
                        </Question>
                    </div>
                </ShowIf>
                <ShowIf showIf={state.borrower?.finalizeThirdPartyClaim === 'yes'}>
                    <div className='row'>
                        <Question
                            type={QuestionType.Text}
                            selector='party-name'
                            styleName='ungrouped'
                            onChange={actions?.onChangeFinalizeThirdPartyName}
                            onQuestionValidate={onQuestionValidate}
                            submitted={submitted}
                            value={state.borrower?.finalizeThirdPartyName}
                        >
                            Party Name
                        </Question>
                    </div>
                </ShowIf>
            </div>
            <div className='button-group'>
                <ShowIf showIf={!formCompleted}>
                    <BackButtonWithStore />
                </ShowIf>
                <button
                    id='submitButton'
                    className='button'
                    style={{ width: '100%' }}
                    disabled={canSubmit()}
                    onClick={submitButtonClick}
                >
                    Submit
                </button>
            </div>
            <EsignModal
                agreementHtml={acknowledgeHtml}
                isOpen={acknowledgeModalOpen}
                signatureText={typedName}
                cancelCallback={onRejectAcknowledge}
                confirmCallback={onConfirmAcknowledge}
                ipAddress={ipAddress}
                font={font}
            />
            <EsignModal
                agreementHtml={termSheetHtml}
                isOpen={termSheetModalOpen}
                signatureText={typedName}
                cancelCallback={onRejectTermsheet}
                confirmCallback={onConfirmTermsheet}
                ipAddress={ipAddress}
                font={font}
            />
        </>
    );
};

export const constructAgreementHtml = (
    template: string,
    name: string,
    title: string,
    date: Date,
    signatureFont?: string,
    ipAddress?: string
) => {
    const base = template
        .replace(/{{#NAME}}/g, name)
        .replace(/{{#TITLE}}/g, title)
        .replace(/{{#DATE}}/g, `${formatDateTime(date)}`)
        .replace(/{{#IPADDRESS}}/g, !ipAddress ? 'unknown' : ipAddress)
        .replace(/{{dateTime}}/g, `${formatDateTime(date)}`)
        .replace(/{{ipAddress}}/g, !ipAddress ? 'unknown' : ipAddress);

    if (signatureFont) {
        return base.replace(/{{#SIGNATURE}}/g, `<span style="font-size: 120%; font-family: '${signatureFont}';">${name}</span>`);
    } else {
        return base.replace(/{{#SIGNATURE}}/g, '__________________________________________');
    }
};

export const agreementHtmlTemplate = `
<p>I have prepared and reviewed this form. I hereby certify and represent that all of the statements are accurate to the best of my knowledge.
I authorize Builder Finance Inc. and its affiliates to obtain references for the business and principals associated with this loan,
including contacting any of the parties (a) listed herein for whom a phone number is requested and/or (b) listed on any other form as a reference.</p>

<p>I hereby certify, represent, and warrant that the loan is made for the business purpose of building the subject property. Such property
is expected to be sold at arm's length to a third party, and the individual comprising the current ownership will not occupy the property
or otherwise allow it to be used personally until the requested BFI loan has been paid off in full.</p>

<p>Signature: {{#SIGNATURE}}</p>

<p>Name: {{#NAME}}</p>

<p>Title: {{#TITLE}}</p>

<p>Date: {{#DATE}}</p>
`;

export const generalAcknowledgementTemplate = `
<p>I have prepared and reviewed this application. I hereby certify and represent that all of statements are accurate to the best of my knowledge. I
authorize Builder Finance Inc. and its affiliates to obtain references for the business and principals associated with this loan, including contacting
any of the parties (a) listed as part of this submission for whom a phone number is requested and/or (b) listed on any other form as a reference.</p>

<p>I hereby certify, represent, and warrant that the property is being built for commercial purposes: it is intended for arm's length sale or rental
to a third party, and the individuals comprising the current ownership will not occupy the property or otherwise allow it to be used personally until
the requested BFI loan has been paid off in full.</p>

<p>Signature: {{#SIGNATURE}}<br/>
Name: {{#NAME}}<br/>
Title: {{#TITLE}}<br/>
Date: {{#DATE}}<br/>
IP Address: {{#IPADDRESS}}</p>
`;

export const rentalAcknowledgementTemplate = `
<p>I have prepared and reviewed this application. I hereby certify and represent that all of statements are accurate to the best of my knowledge. I
hereby certify, represent, and warrant that the property is owned and operated for commercial purposes and that it is, and will be, rented to arm's
length third party(ies) and that individual(s) comprising the current ownership will not occupy the property or otherwise allow it to be used
personally.</p>

<p>Signature: {{#SIGNATURE}}<br/>
Name: {{#NAME}}<br/>
Title: {{#TITLE}}<br/>
Date: {{#DATE}}<br/>
IP Address: {{#IPADDRESS}}</p>
`;

export const mapStateToProps = (state: Store.ApplyRootStateType) => ({
    state: state,
});
export interface Actions {
    onChangeFinalizeThirdPartyClaim?: (newvalue: string | undefined) => void;
    onChangeFinalizeThirdPartyName?: (newvalue: string | undefined) => void;
}
export const mapDispatchToProps = (dispatch: Store.ApplyDispatchType) => {
    const props: { actions: Actions; dispatch: Store.ApplyDispatchType } = {
        actions: {
            onChangeFinalizeThirdPartyClaim: (newvalue: string | undefined) =>
                dispatch(Store.BorrowerStore.Actions.updateFinalizeThirdPartyClaim(newvalue)),
            onChangeFinalizeThirdPartyName: (newvalue: string | undefined) =>
                dispatch(Store.BorrowerStore.Actions.updateFinalizePartyName(newvalue)),
        },
        dispatch,
    };
    return props;
};

export default connect(mapStateToProps, mapDispatchToProps)(ReviewSubmission);
